import type { NewConfig } from "@p0security/shared/integrations/shared";
import { DEFAULT_WIF_REQUEST_PARAMETER } from "@p0security/shared/types/agentic/credential";
import {
  type McpServerInstance,
  type McpServerSpec,
  type McpTool,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";
import { z } from "zod";

import {
  DATABRICKS_AI_INTEGRATION_KEY,
  DatabricksAiMcpServices,
  DatabricksWorkspaceDomains,
} from "./constants.ts";
import { DatabricksAiIconUrl } from "./icon.ts";
import { databricksGenieMcpTools, databricksSqlMcpTools } from "./mcp-tools.ts";

/**
 * The resolver shape of agentic's `McpServerSpecResolver`, restated here:
 * databricks-ai-shared can't import agentic-shared, which imports it for the
 * MCP registry.
 */
type DatabricksAiMcpServerSpecResolver = (location: {
  gateway: string;
  server: string;
  instance?: string;
}) => McpServerSpec & { type: "external" };

/** What `convertServerSpec` replaces with the server's instance in its URL. */
const INSTANCE_PLACEHOLDER = "{instance}";

/**
 * The workspace each added server covers, by its deployment name. The admin
 * gives it once, when adding the server.
 */
const workspaceInstance: McpServerInstance = {
  description:
    "The workspace's deployment name, the part of its URL before .cloud.databricks.com. Add one Databricks Genie and one Databricks SQL server for each workspace agents use.",
  label: "Workspace instance",
  // A DNS label in lowercase, which is what Databricks names a workspace's
  // host with.
  pattern: /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  placeholder: "dbc-a1b2c3d4-e5f6",
};

/**
 * The `https://` origin of the workspace with this deployment name. Stage
 * compares it with the workspace's host, so a server only gets grants in its
 * own workspace.
 */
export const databricksAiWorkspaceHost = (instance: string) =>
  `https://${instance}.${DatabricksWorkspaceDomains["https://accounts.cloud.databricks.com"]}`;

// The INT-810 live check on 2026-10-21 measured 48 seconds from the last
// grant to the first successful tool call, and 1 second from the federation
// policy to the first token. The maximum leaves room for slower workspaces.
const databricksAiPropagationHint = `${propagationHint({
  maxWait: "2 minutes",
  normalWait: "30 to 60 seconds",
  pollInterval: "10 seconds",
  resource: "Databricks",
})}

A grant requested through the other Databricks server doesn't work here. Request again through this server.`;

const prefixArgs = ["databricks-ai", "data"];

// The last sentence is there because the principal's federation policy
// trusts the gateway's subject for one server only.
const accessPromptFor = (
  host: string | undefined
) => `argv: [<workspace>, <warehouse>, <catalog>, <level>, "--schema", <schema>?, ("--table", <table> | "--volume", <volume>)?, "--reason", <reason>]
- workspace: workspace ID. Use list.${host ? ` This server covers only the workspace at ${host}.` : ""}
- warehouse: SQL warehouse ID in that workspace. Your queries run on it. Use list with workspace.
- catalog, schema, table, volume: name the narrowest object you need. The grant covers it and everything under it. Use list.
- level: 'read' to query and read. 'write' to also modify tables and volumes, and, on a catalog or schema, to create tables, volumes, functions and materialized views.
Each grant covers one object in one workspace, on this server only. Request again through the other server to use its tools. To cover several tables, request their schema.
`;

/**
 * One Databricks MCP server on the Unity Gateway, at one workspace's MCP
 * service. The admin adds one per workspace, and its instance names it.
 *
 * The agent signs in as the grant's own service principal. The gateway
 * exchanges a token P0's auth server signs for that principal's Databricks
 * token at the workspace's token endpoint, which the principal's federation
 * policy allows. Stage refuses a request for any other workspace, so the
 * grant's workspace is the one the gateway sends that token to.
 */
const newDatabricksAiMcpServer =
  (server: {
    label: string;
    /** The MCP service in `system.ai`, e.g. `system.ai.dbsql`. */
    service: string;
    tools: McpTool[];
  }): DatabricksAiMcpServerSpecResolver =>
  ({ instance }) => {
    const { label, service, tools } = server;
    // Undefined while the catalog is listed. `convertServerSpec` refuses an
    // invalid one, but only after this runs.
    const valid =
      instance !== undefined && workspaceInstance.pattern.test(instance)
        ? instance
        : undefined;
    const host = valid ? databricksAiWorkspaceHost(valid) : undefined;
    return {
      access: {
        prefixArgs,
        prompt: accessPromptFor(host),
        propagationHint: databricksAiPropagationHint,
      },
      // Unity Catalog privileges decide what each tool can reach.
      authorization: "upstream",
      credential: {
        claims: '{ "aud": {{ request.generated.accountId | json }} }',
        csp: "generic",
        parameter: DEFAULT_WIF_REQUEST_PARAMETER,
        requestParams: {
          // The grant's own principal, which stage recorded.
          client_id: "{{ request.generated.applicationId }}",
          scope: "all-apis",
        },
        tokenEndpoint: `${databricksAiWorkspaceHost(valid ?? INSTANCE_PLACEHOLDER)}/oidc/v1/token`,
        type: "wif",
      },
      iconUrl: DatabricksAiIconUrl,
      instance: workspaceInstance,
      label: valid ? `${label} (${valid})` : label,
      list: {
        // In the order an agent narrows them down.
        fields: {
          workspace: {
            description: "Databricks workspace, by ID",
            required: [],
          },
          warehouse: {
            description: "Pro or serverless SQL warehouse in the workspace",
            required: ["workspace"],
          },
          catalog: {
            description: "Unity Catalog catalog the workspace can use",
            required: ["workspace"],
          },
          schema: {
            description: "Schema in the catalog",
            required: ["workspace", "catalog"],
          },
          table: {
            description: "Table, view or materialized view in the schema",
            required: ["workspace", "catalog", "schema"],
          },
          volume: {
            description: "Volume in the schema",
            required: ["workspace", "catalog", "schema"],
          },
          level: { description: "read, or write", required: [] },
        },
        prefixArgs,
      },
      tools: { tools, type: "manual" },
      type: "external",
      url: `${databricksAiWorkspaceHost(INSTANCE_PLACEHOLDER)}/ai-gateway/mcp-services/${service}`,
    };
  };

/**
 * The two Databricks servers, each added once per workspace. The keys are
 * stored as each added server's `definition.id`, so they must never change.
 */
export const databricksAiMcpServers = {
  databricksGenie: newDatabricksAiMcpServer({
    label: "Databricks Genie",
    service: DatabricksAiMcpServices.databricksGenie,
    tools: databricksGenieMcpTools,
  }),
  databricksSql: newDatabricksAiMcpServer({
    label: "Databricks SQL",
    service: DatabricksAiMcpServices.databricksSql,
    tools: databricksSqlMcpTools,
  }),
};

/**
 * The installed config's accounts, read only as far as availability needs.
 * A config that isn't shaped like this offers neither server.
 */
const accountsConfig = z
  .object({
    account: z
      .record(
        z.string(),
        z.object({ state: z.unknown(), unityGatewayBeta: z.unknown() })
      )
      .optional(),
  })
  .catch({});

const listInstalledAccounts = (installed: Record<string, NewConfig>) => {
  const { account = {} } = accountsConfig.parse(
    installed[DATABRICKS_AI_INTEGRATION_KEY] ?? {}
  );
  return Object.values(account).filter(({ state }) => state === "installed");
};

/**
 * Whether each server can be added. Genie needs an installed account, and
 * Databricks SQL one whose Unity Gateway beta was on when it was last
 * saved. The field holds a string.
 */
export const databricksAiMcpServerAvailability = {
  databricksGenie: (installed: Record<string, NewConfig>): boolean =>
    listInstalledAccounts(installed).length > 0,
  databricksSql: (installed: Record<string, NewConfig>): boolean =>
    listInstalledAccounts(installed).some(
      (account) => account?.unityGatewayBeta === "true"
    ),
};
